import { IconChevronDown, IconPlus } from "@pentabd/icons";
import {
  Button,
  InputSelect,
  InputText,
  Select,
  Switch,
  Text,
} from "@pentabd/ui";
import React, { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import CommitteeTable from "./CommitteeTable";
import AddCommitteeMemberModal from "../AddCommitteeMemberModal";
import {
  CommitteeCreatePageProps,
  CommitteeCreateType,
  SelectedMemberType,
} from "@type/procurements/committee";
import useActionCreator from "@redux/hooks/useActionCreator";
import { useNavigate, useParams } from "react-router-dom";
import useCustomSelector from "@redux/hooks/useCustomSelector";
import { ROUTES } from "@constants/routes";
import {
  CancelButton,
  ErrorModal,
  MultiFileRow,
  SelectControllerRow,
  SuccessModal,
} from "@components";
import { useTranslation } from "react-i18next";
import {
  convertMultipleDocumentData,
  revertMultipleDocumentData,
} from "@utils/documentDataConvert";

const COMMITTEE_TYPE = [
  { label: "Purchase Committee", value: "PURCHASE_COMMITTEE" },
  { label: "Opening Committee", value: "OPENING_COMMITTEE" },
  { label: "Evaluation Committee", value: "EVALUATION_COMMITTEE" },
  { label: "Final Committee", value: "FINAL_COMMITTEE" },
];

export default function CreateCommitteeForm({
  committeeState,
}: CommitteeCreatePageProps) {
  const navigate = useNavigate();
  const { id } = useParams();
  const { t } = useTranslation();

  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState<boolean>(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState<boolean>(false);

  const [isOpenModal, setIsOpenModal] = useState(false);

  const { getSingleData } = committeeState;

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    setValue,
    reset,
  } = useForm<CommitteeCreateType>({
    values: revertMultipleDocumentData(committeeState?.getSingleData, [
      "committeeDocuments",
    ]),
  });

  const { createSuccess, createError, updateSuccess, updateError } =
    useCustomSelector((store) => store.procurements.committeeState);

  const [selectedMembers, setSelectedMembers] = useState<
    SelectedMemberType[] | []
  >([]);

  const { committeeCreateActionHandler, committeeUpdateActionHandler } =
    useActionCreator();

  const openAddCommitteeModal = () => {
    setIsOpenModal(true);
  };

  const closeAddCommitteeModal = () => {
    setIsOpenModal(false);
  };

  const addEmployeeModalAction = (ids: (string | number)[], employees: any) => {
    ids?.forEach((item: any) => {
      let employee = employees?.find((emp: any) => emp?.id === item);

      setSelectedMembers((prev) => {
        if (employee && prev) {
          let isExist = prev?.some((prevItem) => prevItem?.id === employee?.id);
          if (!isExist) {
            return [
              ...prev,
              {
                id: employee?.id || "",
                nameEn: employee?.nameEn || "",
                nameBn: employee?.nameBn || "",
                label: employee?.nameEn || "",
                value: employee?.id || "",
                employeeMasterId: employee?.id || "",
                designation: employee?.designation || "",
                isCommitteeHead: false,
                memberType: "MEMBER",
              },
            ];
          }
        }
        return prev;
      });
    });
  };

  const selectCommitteeHead = (id: number) => {
    if (id) {
      setValue("committeeHeadId", id);

      setSelectedMembers((prev) => {
        return prev.map((item) => {
          if (item.id === id && item.memberType !== "CHAIRPERSON") {
            item.memberType = "CHAIRPERSON";
          } else if (item.id !== id && item.memberType === "CHAIRPERSON") {
            item.memberType = "MEMBER";
          }
          return item;
        });
      });
    }
  };

  const selectSecretary = (id: number) => {
    if (id) {
      setValue("committeeSecretaryId", id);
      setSelectedMembers((prev) => {
        return prev.map((item) => {
          if (item.id === id && item.memberType !== "SECRETARY") {
            item.memberType = "SECRETARY";
          } else if (item.id !== id && item.memberType === "SECRETARY") {
            item.memberType = "MEMBER";
          }
          return item;
        });
      });
    }
  };
  const deleteCommitteeMember = (id: number) => {
    setSelectedMembers((prev: SelectedMemberType[] | []) => {
      const filteredData = prev.filter(
        (item: SelectedMemberType) => item.id !== id,
      );

      const noCommitteeHead = filteredData.every(
        (item) => !item.isCommitteeHead,
      );

      if (noCommitteeHead) {
        setValue("committeeHeadId", "");
      }

      return filteredData;
    });
  };

  const createSuccessAction = () => {
    reset();
    setIsSuccessModalOpen(false);
    navigate(`${ROUTES.PROCUREMENT.COMMITTEE}`);
  };

  const closeErrorModal = () => {
    setIsErrorModalOpen(false);
  };

  const onSubmit: SubmitHandler<CommitteeCreateType> = (
    data: CommitteeCreateType,
  ) => {
    const formattedData = {
      ...data,
      committeeType: data?.committeeType || null,
      committeeMembers: selectedMembers,
    };
    if (id) {
      committeeUpdateActionHandler(
        id,
        convertMultipleDocumentData(formattedData, ["committeeDocuments"]),
      );
    } else {
      committeeCreateActionHandler(
        convertMultipleDocumentData(formattedData, ["committeeDocuments"]),
      );
    }
  };

  useEffect(() => {
    if (createSuccess || updateSuccess) {
      setIsSuccessModalOpen(true);
    }

    if (createError || updateError) {
      setIsErrorModalOpen(true);
    }
  }, [createSuccess, updateSuccess, createError, updateError]);

  useEffect(() => {
    if (committeeState.getSingleData) {
      let formattedCommitteeMembers =
        committeeState.getSingleData.committeeMembers?.map((item: any) => {
          const newItem = {
            id: item.employee?.id,
            employeeMasterId: item.employee?.id,
            nameEn: item.employee?.nameEn || "",
            nameBn: item.employee?.nameBn || "",
            label: item.employee?.nameEn || "",
            value: item.employee?.id,
            designation: item.employee?.designation || "",
            isCommitteeHead: item?.isCommitteeHead,
            memberType: item?.memberType || "",
          };
          if (item?.memberType === "CHAIRPERSON") {
            setValue("committeeHeadId", newItem.id);
          }

          if (item?.memberType === "SECRETARY") {
            setValue("committeeSecretaryId", newItem.id);
          }

          return newItem;
        });

      setSelectedMembers((prev) => {
        return [...prev, ...formattedCommitteeMembers];
      });
    }
  }, [getSingleData]);

  return (
    <div>
      {/* modals  */}
      {isOpenModal && (
        <AddCommitteeMemberModal
          isModalOpen={isOpenModal}
          closeModal={closeAddCommitteeModal}
          onSaveAction={addEmployeeModalAction}
        />
      )}

      {isSuccessModalOpen && (
        <SuccessModal
          isModalOpen={isSuccessModalOpen}
          action={createSuccessAction}
          subtitle={`You have ${
            id ? "updated" : "created"
          } a procurement committee`}
        />
      )}
      {isErrorModalOpen && (
        <ErrorModal
          subtitle={createError.response.data.message || "something was wrong"}
          isModalOpen={isErrorModalOpen}
          closeModal={closeErrorModal}
        />
      )}
      {/* modals end */}
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="container p-10">
          <div className="d-flex flex-column gap-10 pb-10 border-bottom">
            <div className="row">
              <div className="col-md-12 col-lg-3">
                <Text weight="semibold" color="title" size="sm" component="p">
                  {t("COMMITTEE.FORM.NAME.LABEL")}
                </Text>
              </div>
              <div className="col-md-12 col-lg-7">
                <InputText
                  type="text"
                  {...register("name", {
                    required: "Committee name is required",
                  })}
                  placeholder={t("COMMITTEE.FORM.NAME.PLACEHOLDER")}
                />
                {errors.name && (
                  <Text color="danger" component="p" size="sm" className="mt-2">
                    {errors.name.message || ""}
                  </Text>
                )}
              </div>
            </div>

            <div className="row">
              <div className="col-md-12 col-lg-3">
                <Text weight="semibold" color="title" size="sm" component="p">
                  {t("COMMITTEE.FORM.SELECT_MEMBERS.LABEL")}
                </Text>
              </div>
              <div className="col-md-12 col-lg-7">
                <Button type="info" fill="fill" onClick={openAddCommitteeModal}>
                  <IconPlus fill="light" size={24} />
                  {t("COMMITTEE.FORM.SELECT_MEMBERS.BUTTON_TEXT")}
                </Button>
              </div>
            </div>

            <div className="row">
              <div className="col-md-12 col-lg-3">
                <Text weight="semibold" color="title" size="sm" component="p">
                  {t("COMMITTEE.FORM.SELECTED_MEMBERS.LABEL")}
                </Text>
              </div>
              <div className="col-md-12 col-lg-7">
                <CommitteeTable
                  selectedMembers={selectedMembers}
                  deleteCommitteeMember={deleteCommitteeMember}
                />
              </div>
            </div>

            <SelectControllerRow
              name="committeeType"
              register={{
                ...register("committeeType", {
                  required: "committee type is required",
                }),
              }}
              control={control}
              error={errors as any}
              label="COMMITTEE.FORM.COMMITTEE_TYPE"
              options={COMMITTEE_TYPE}
              setValue={setValue}
              required
            />

            <div className="row">
              <div className="col-md-12 col-lg-3">
                <Text weight="semibold" color="title" size="sm" component="p">
                  {t("COMMITTEE.FORM.COMMITTEE_PRESIDENT.LABEL")}
                </Text>
              </div>
              <div className="col-md-12 col-lg-7">
                <Controller
                  control={control}
                  name="committeeHeadId"
                  render={({ field }) => {
                    return (
                      <Select
                        {...register("committeeHeadId", {
                          required: "Committee head is required",
                        })}
                        options={selectedMembers}
                        placeholder={t(
                          "COMMITTEE.FORM.COMMITTEE_PRESIDENT.PLACEHOLDER",
                        )}
                        defaultValue={field.value as number}
                        onSelectItem={(value) => {
                          selectCommitteeHead(value as number);
                        }}
                        suffix={<IconChevronDown size="16" />}
                      />
                    );
                  }}
                />

                {errors?.committeeHeadId && (
                  <Text color="danger" component="p" size="sm" className="mt-2">
                    {"Committee head is required"}
                  </Text>
                )}
              </div>
            </div>

            <div className="row">
              <div className="col-md-12 col-lg-3">
                <Text weight="semibold" color="title" size="sm" component="p">
                  {t("COMMITTEE.FORM.COMMITTEE_SECRETARY.LABEL")}
                </Text>
              </div>
              <div className="col-md-12 col-lg-7">
                <Controller
                  control={control}
                  name="committeeSecretaryId"
                  render={({ field }) => {
                    return (
                      <Select
                        {...register("committeeSecretaryId")}
                        options={selectedMembers}
                        placeholder={t(
                          "COMMITTEE.FORM.COMMITTEE_SECRETARY.PLACEHOLDER",
                        )}
                        defaultValue={field.value as number}
                        onSelectItem={(value) => {
                          selectSecretary(value as number);
                        }}
                        suffix={<IconChevronDown size="16" />}
                      />
                    );
                  }}
                />

                {/* {errors?.committeeSecretaryId && (
                  <Text color="danger" component="p" size="sm" className="mt-2">
                    {"Committee head is required"}
                  </Text>
                )} */}
              </div>
            </div>

            <div className="row">
              <div className="col-lg-3">
                <Text weight="semibold" size="sm" color="title">
                  {t("COMMITTEE.FORM.STATUS.LABEL")}
                </Text>
              </div>
              <div className="col-lg-7 d-flex flex-column gap-4">
                <Switch
                  {...register("isActive")}
                  id="isActive"
                  name="isActive"
                  size="sm"
                  onChange={(e) => setValue("isActive", e.target.checked)}
                  controlling
                />
              </div>
            </div>

            <MultiFileRow
              label="RESIGNATION.FORM.SUPPORTING_DOC.UPLOAD_DOCUMENT.EMPLOYEE"
              name={"committeeDocuments"}
              control={control}
              setValue={setValue}
              register={register}
              fileDetails={["documentName", "date", "remarks", "memorialNo"]}
            />
          </div>

          <div className="col-md-12 col-lg-10 d-flex justify-content-end mt-10 gap-8">
            <CancelButton />

            <Button fill="fill" htmlType="submit" type="primary" size="sm">
              {t("COMMITTEE.FORM.BUTTONS.SAVE")}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}
